import { useMemo } from "react";
import { Logo } from "@components";
import PhoneFrame from "./PhoneFrame";
import { formatAmount, getMonth } from "@utils";

const defaultCustomerRow = {
  customerName: "John Doe",
  reference: "ACC-CODE",
  overdue: 1240.32,
  dueThisMonth: 191.66,
  balance: 1931.98,
};

const OpenRequestPreview = ({
  merchant,
  customerRow: customerRowProp,
  invoicesRequested,
  mandatory = false,
  selected = true,
  previewTokens,
}) => {
  const customerRow = customerRowProp || defaultCustomerRow;
  const primaryColour =
    merchant?.customisationSettings?.colourSet?.primary ?? "#991a2d";
  const buttonTextColour =
    merchant?.customisationSettings?.colourSet?.buttonText ?? "#ffffff";

  const isCustom = invoicesRequested === "Custom";
  const amountDisplay =
    previewTokens?.find((t) => t.token === "amount_requested")?.value ??
    formatAmount(123.45);

  if (isCustom) {
    return (
      <PhoneFrame>
        <div className="flex flex-col h-full bg-white">
          <PreviewHeader
            merchant={merchant}
            primaryColour={primaryColour}
          />

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-4">
            <div className="py-5 border-b border-zinc-200 text-center">
              <h2 className="text-[11px] font-semibold text-zinc-900 mb-0.5">
                Payment amount
              </h2>
              <p className="text-lg font-medium text-zinc-900">
                {amountDisplay}
              </p>
            </div>
            <div className="py-4 border-b border-zinc-200">
              <p className="text-[9px] text-zinc-500 mb-0.5">Name</p>
              <p className="text-[11px] font-medium text-zinc-900 mb-3">
                {customerRow?.customerName ?? "Customer"}
              </p>
              <p className="text-[9px] text-zinc-500 mb-0.5">Reference</p>
              <p className="text-[11px] font-medium text-zinc-900">
                {customerRow?.reference ?? "—"}
              </p>
            </div>
            <div className="px-1 pt-4">
              <button
                type="button"
                className="py-1.5 text-center rounded-lg font-medium text-[10px] w-full"
                style={{ backgroundColor: primaryColour, color: buttonTextColour }}
              >
                Confirm and continue
              </button>
            </div>
          </div>
        </div>
      </PhoneFrame>
    );
  }

  return (
    <InvoiceRequestPreview
      merchant={merchant}
      customerRow={customerRow}
      invoicesRequested={invoicesRequested}
      mandatory={mandatory}
      selected={selected}
      primaryColour={primaryColour}
      buttonTextColour={buttonTextColour}
    />
  );
};

/* ---------- Shared header ---------- */

function PreviewHeader({ merchant, primaryColour }) {
  return (
    <>
      <div className="shrink-0 flex items-center justify-center py-2 h-[48px] bg-white">
        <Logo
          type="center"
          brand={merchant?.organisationId}
          height="28px"
          width="120px"
        />
      </div>
      <div className="shrink-0 w-full h-[3px] bg-zinc-200">
        <div
          className="h-full w-[20%]"
          style={{ backgroundColor: primaryColour }}
        />
      </div>
    </>
  );
}

/* ---------- Invoice request layout ---------- */

function InvoiceRequestPreview({
  merchant,
  customerRow,
  invoicesRequested,
  mandatory,
  selected,
  primaryColour,
  buttonTextColour,
}) {
  const groups = useMemo(() => {
    const overdue = customerRow?.overdue || 0;
    const dueThisMonth = customerRow?.dueThisMonth || 0;
    const balance = customerRow?.balance || 0;
    const futureAmount = Math.max(balance - overdue - dueThisMonth, 0);

    const nextMonth = getMonth(1);

    switch (invoicesRequested) {
      // Specific invoices aren't a bucket breakdown — they resolve to one required group carrying the
      // selected invoices' total (the resolved display amount, stamped on the row's minimumAmount).
      case "SpecificInvoices":
        return [
          {
            label: "Specific invoices",
            amount: customerRow?.minimumAmount || 0,
            requested: true,
          },
        ];

      case "Overdue":
        return [
          { label: "Overdue", amount: overdue, requested: true },
          { label: "Due this month", amount: dueThisMonth, requested: false },
          ...(futureAmount > 0
            ? [
                {
                  label: `Due in ${nextMonth}`,
                  amount: futureAmount,
                  requested: false,
                },
              ]
            : []),
        ];

      case "OverdueAndDueThisMonth":
        return [
          { label: "Overdue", amount: overdue, requested: true },
          { label: "Due this month", amount: dueThisMonth, requested: true },
          ...(futureAmount > 0
            ? [
                {
                  label: `Due in ${nextMonth}`,
                  amount: futureAmount,
                  requested: false,
                },
              ]
            : []),
        ];

      case "All":
        return [
          { label: "Overdue", amount: overdue, requested: true },
          { label: "Due this month", amount: dueThisMonth, requested: true },
          ...(futureAmount > 0
            ? [
                {
                  label: `Due in ${nextMonth}`,
                  amount: futureAmount,
                  requested: true,
                },
              ]
            : []),
        ];

      case "None":
      default:
        return [
          { label: "Overdue", amount: overdue, requested: false },
          { label: "Due this month", amount: dueThisMonth, requested: false },
          ...(futureAmount > 0
            ? [
                {
                  label: `Due in ${nextMonth}`,
                  amount: futureAmount,
                  requested: false,
                },
              ]
            : []),
        ];
    }
  }, [customerRow, invoicesRequested]);

  const selectedTotal = useMemo(
    () =>
      groups
        .filter((g) => selected && g.requested)
        .reduce((sum, g) => sum + g.amount, 0),
    [groups, selected],
  );

  const hasSelection = selected && groups.some((g) => g.requested);

  return (
    <PhoneFrame>
      <div className="flex flex-col h-full bg-white">
        <PreviewHeader merchant={merchant} primaryColour={primaryColour} />

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="px-4 pt-3 pb-2">
            <h2 className="text-xs font-medium text-zinc-900">
              Select invoices to pay
            </h2>
            <p className="text-[9px] text-zinc-500 font-light mt-0.5">
              Choose the invoices you wish to settle today
            </p>
          </div>

          {/* Invoice group cards */}
          <div className="px-4 flex flex-col gap-1.5">
            {groups.map((group) => (
              <InvoiceGroupCard
                key={group.label}
                label={group.label}
                amount={group.amount}
                checked={selected && group.requested}
                requested={group.requested}
                mandatory={mandatory && group.requested}
              />
            ))}
          </div>
        </div>

        {/* Payment summary footer */}
        <div className="shrink-0 border-t border-zinc-200 bg-white p-3">
          <div className="flex justify-between items-center mb-1 text-zinc-600 font-light text-[9px]">
            <span>Invoices total</span>
            <span>{formatAmount(selectedTotal)}</span>
          </div>
          <div className="flex justify-between items-center py-1 font-normal text-[10px] border-t border-zinc-600">
            <span>You will pay</span>
            <span>{formatAmount(selectedTotal)}</span>
          </div>
          <button
            type="button"
            disabled={!hasSelection}
            className={`py-1.5 text-center rounded-lg font-medium text-[9px] w-full mt-2 ${!hasSelection ? "opacity-30" : ""}`}
            style={{ backgroundColor: primaryColour, color: buttonTextColour }}
          >
            Confirm and continue
          </button>
        </div>
      </div>
    </PhoneFrame>
  );
}

/* ---------- Invoice group card ---------- */

function InvoiceGroupCard({ label, amount, checked, requested, mandatory }) {
  return (
    <div className="border border-zinc-200 rounded-lg overflow-hidden bg-white shadow-sm">
      <div className="flex items-start p-2.5">
        {/* Checkbox */}
        <div className="shrink-0 mr-1.5 mt-0.5">
          <div
            className={`size-3 rounded border flex items-center justify-center ${
              checked
                ? "bg-primary-600 border-primary-600"
                : "border-zinc-300 bg-white"
            } ${mandatory ? "opacity-50" : ""}`}
          >
            {checked && (
              <svg
                viewBox="0 0 14 14"
                fill="none"
                className="size-2 stroke-white"
              >
                <path
                  d="M3 8L6 11L11 3.5"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </div>
        </div>

        <div className="flex flex-col grow gap-0.5">
          <div className="flex justify-between items-start">
            <span className="text-[10px] font-normal text-zinc-900 flex items-center gap-1">
              {label}
              {requested && (
                <span className="inline-flex items-center rounded-sm h-3 px-0.5 text-[7px] font-normal ring-1 ring-inset bg-blue-500/5 text-blue-500 ring-blue-500">
                  {mandatory ? "Required" : "Requested"}
                </span>
              )}
            </span>
            <span className="text-[10px] font-normal text-zinc-900 text-right whitespace-nowrap ml-1">
              {formatAmount(amount)}
            </span>
          </div>
          <div className="text-[8px]">
            <span className="text-zinc-500 font-light">
              {checked ? "All invoices selected" : "No invoices selected"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OpenRequestPreview;
